import { WorkbookCell } from "./xlsx";

/** Accepts only digits, separators and a leading sign — anything with a unit stays text. */
const NUMERIC_SHAPE = /^[-+]?[\d.,]+$/;

type DecimalSeparator = "." | ",";

/**
 * Reads a whole column of exported cells as numbers.
 *
 * Works on the column rather than the cell because a single value is often ambiguous while the
 * column as a whole is not: "1.234" is one thousand two hundred thirty-four in German and one
 * point two three four in English, but a sibling value of "1.234.567" settles it. Deciding once
 * per column also rules out the worst outcome — a column where some cells became numbers and
 * others stayed text, which makes Excel's SUM quietly wrong with nothing on screen to show it.
 *
 * A column that stays ambiguous throughout is left as text. Being visibly unconverted is far
 * less harmful than being silently off by a factor of 1000.
 *
 * @param values - The column's cells, in row order.
 * @returns One entry per input: the number, or `null` to leave that cell as text.
 */
export function parseNumericColumn(values: WorkbookCell[]): (number | null)[] {
  const separator = inferDecimalSeparator(values);

  return values.map(value => {
    if (typeof value === "number") {
      return Number.isFinite(value) ? value : null;
    }
    if (typeof value !== "string") {
      return null;
    }

    const trimmed = value.trim();
    if (trimmed === "" || !NUMERIC_SHAPE.test(trimmed)) {
      return null;
    }

    const dots = countOf(trimmed, ".");
    const commas = countOf(trimmed, ",");

    if (dots === 0 && commas === 0) {
      return toFiniteNumber(trimmed);
    }

    // Still ambiguous after looking at the whole column: leave the cell — and therefore the
    // whole column, since every value here is ambiguous — as text.
    if (separator === null) {
      return null;
    }

    const grouping = separator === "." ? "," : ".";
    const normalized = trimmed.split(grouping).join("").replace(separator, ".");
    return toFiniteNumber(normalized);
  });
}

/**
 * Finds the column's decimal separator from the first value that can only be read one way, or
 * `null` when every value could be read either way.
 */
function inferDecimalSeparator(values: WorkbookCell[]): DecimalSeparator | null {
  for (const value of values) {
    if (typeof value !== "string") {
      continue;
    }
    const trimmed = value.trim();
    if (trimmed === "" || !NUMERIC_SHAPE.test(trimmed)) {
      continue;
    }

    const dots = countOf(trimmed, ".");
    const commas = countOf(trimmed, ",");

    // Both kinds present: the last one separates the decimals, the other groups.
    if (dots > 0 && commas > 0) {
      return trimmed.lastIndexOf(",") > trimmed.lastIndexOf(".") ? "," : ".";
    }

    // One kind, repeated: it can only be grouping, so the other kind is the decimal separator.
    if (dots > 1) {
      return ",";
    }
    if (commas > 1) {
      return ".";
    }

    if (dots === 1 || commas === 1) {
      const candidate: DecimalSeparator = dots === 1 ? "." : ",";
      const at = trimmed.lastIndexOf(candidate);
      const digitsBefore = trimmed.slice(0, at).replace(/^[-+]/, "").length;
      const digitsAfter = trimmed.length - at - 1;

      // A thousands group has at most three digits ahead of the separator and exactly three
      // behind it. Anything else could not have been produced by grouping.
      if (digitsAfter !== 3 || digitsBefore > 3) {
        return candidate;
      }
    }
  }

  return null;
}

/**
 * Counted with `indexOf` rather than iterating the string: this runs up to four times per cell,
 * and a `for…of` allocates an iterator and decodes code points for what is a search for one
 * plain ASCII character.
 */
function countOf(value: string, character: string): number {
  let count = 0;
  let index = value.indexOf(character);
  while (index !== -1) {
    count++;
    index = value.indexOf(character, index + 1);
  }
  return count;
}

function toFiniteNumber(value: string): number | null {
  // Number("") is 0, so a value left without digits — a lone separator, say — must be rejected
  // here rather than becoming a zero.
  if (!/\d/.test(value)) {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}
